import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import { useRegistrationLink, useRotateRegistrationLink } from '../queries/useIdentity';
import styles from './Settings.module.css';
import { SectionHead } from './SettingsScreen';

/**
 * Sekretny link firmy — **jedyna przepustka do rejestracji** (spec 4A/10).
 *
 * Jeden link na całą firmę, wielokrotnego użytku. Nie ma zaproszeń imiennych ani maili:
 * link krąży wewnętrznie, a bramką jest **ręczna aktywacja konta przez admina**. Dlatego
 * sekcja mówi wprost, co się dzieje po kliknięciu — inaczej wygląda jak dziura
 * w bezpieczeństwie, a jest świadomym uproszczeniem MVP.
 */
export function InviteSection() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const link = useRegistrationLink();
  const rotate = useRotateRegistrationLink();
  const [confirming, setConfirming] = useState(false);

  const url = link.data ? `${globalThis.location.origin}${link.data.path}` : '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      show({ message: t('settings.invite.copied'), tone: 'success' });
    } catch {
      // Schowek bywa niedostępny (brak zgody, kontekst bez HTTPS). Cisza byłaby gorsza
      // niż komunikat — człowiek kliknął i musi wiedzieć, że nic się nie stało.
      show({ message: t('settings.invite.copyFailed'), tone: 'danger' });
    }
  }

  return (
    <>
      <SectionHead titleKey="settings.section.invite" leadKey="settings.invite.lead" />

      <section className={styles['card']}>
        <header className={styles['cardHead']}>
          <h2 className={styles['cardTitle']}>{t('settings.invite.title')}</h2>
        </header>

        <div className={styles['cardBody']}>
          {link.isPending ? (
            <div className={styles['loading']} aria-busy="true">
              <Skeleton height="3rem" announce />
            </div>
          ) : link.isError ? (
            <div className={styles['failure']} role="alert">
              <p>{describeError(link.error)}</p>
              <Button onClick={() => void link.refetch()}>{t('common.action.retry')}</Button>
            </div>
          ) : (
            <>
              <p className={styles['secret']}>{url}</p>

              <div className={styles['row']}>
                <span className={styles['rowLabel']}>{t('settings.invite.createdAt')}</span>
                <span className={styles['rowValue']}>{link.data.createdAtUtc.slice(0, 10)}</span>
              </div>

              <div className={styles['inlineForm']}>
                <Button variant="primary" onClick={() => void copy()}>
                  {t('settings.invite.copy')}
                </Button>

                {/*
                  Odnowienie jest NIEODWRACALNE — poprzedni link przestaje działać, więc
                  każdy, kto go dostał i jeszcze nie założył konta, zostaje za drzwiami.
                  Stąd dwa kroki zamiast jednego kliknięcia.
                */}
                {confirming ? (
                  <>
                    <Button
                      variant="danger"
                      disabled={rotate.isPending}
                      onClick={() =>
                        rotate.mutate(undefined, {
                          onSuccess: () => {
                            setConfirming(false);
                            show({ message: t('settings.invite.rotated'), tone: 'success' });
                          },
                          onError: (error) =>
                            show({ message: describeError(error), tone: 'danger' }),
                        })
                      }
                    >
                      {t('settings.invite.rotateConfirm')}
                    </Button>

                    <Button variant="ghost" onClick={() => setConfirming(false)}>
                      {t('common.action.cancel')}
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" onClick={() => setConfirming(true)}>
                    {t('settings.invite.rotate')}
                  </Button>
                )}
              </div>

              {confirming ? <p className={styles['warn']}>{t('settings.invite.rotateWarn')}</p> : null}
            </>
          )}
        </div>
      </section>
    </>
  );
}
